import React, {useCallback, useMemo, useRef, useState} from "react";
import {Stepper} from "@stepperize/react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlus} from "@fortawesome/pro-solid-svg-icons";
import {faCloudArrowUp, faFilePdf, faTrash} from "@fortawesome/pro-regular-svg-icons";
import {CreateProjectStep} from "../stepper/create-project-steps.ts";
import Button from "@/components/buttons/button";
import useProjectCreationContext from "../context/use-project-creation-context.ts";
import Input from "@/components/input";
import Label from "@/components/label";
import FlowMode from "@/models/enums/flow-mode.ts";

interface ProjectInformationsStepProps {
    stepper: Stepper<CreateProjectStep[]>;
}

const CreateProjectImportPlansStep: React.FC<ProjectInformationsStepProps> = ({stepper}) => {
    
    const {flowModeCreationProject, currentFiles, storeyHeight, analyseFileNames, createProject} = useProjectCreationContext();
    
    const {prev, next} = stepper;
    const [files, setFiles] = useState<File[]>(currentFiles);
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef<number>(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const storeyHeightRef = useRef<number>(storeyHeight);

    const handleAddFiles = useCallback((incomingFiles: FileList | null) => {
        if (!incomingFiles || incomingFiles.length === 0) return;

        const pdfFiles: File[] = [];
        for (let i = 0; i < incomingFiles.length; i += 1) {
            const file = incomingFiles[i];
            if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
                pdfFiles.push(file);
            }
        }

        if (pdfFiles.length === 0) return;

        setFiles((prevFiles) => {
            const existingKeys = new Set(
                prevFiles.map((f) => f.name)
            );
            const uniqueNewFiles = pdfFiles.filter(
                (f) => !existingKeys.has(f.name)
            );
            return [...prevFiles, ...uniqueNewFiles];
        });
    }, []);

    const handleRemoveFile = useCallback((indexToRemove: number) => {
        setFiles((prevFiles) => prevFiles.filter((_, index) => index !== indexToRemove));
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            e.dataTransfer.dropEffect = "copy";
        }
    }, []);

    const handleDragEnter= useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        if (!isDragging) {
            setIsDragging(true);
        }
    }, [isDragging]);

    const handleDragLeave= useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) {
            setIsDragging(false);
        }
    }, []);

    const handleDrop= useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = 0;
        setIsDragging(false);

        if (e.dataTransfer.files) {
            handleAddFiles(e.dataTransfer.files);
        }
    }, [handleAddFiles]);

    const handleFileInputChange= useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            handleAddFiles(e.target.files);
        }
        e.target.value = "";
    }, [handleAddFiles]);

    const openFileDialog = useCallback(() => {
        fileInputRef.current?.click();
    }, []);
    
    const handleClickContinue = useCallback(() => {
        const projectData = analyseFileNames(files, storeyHeightRef.current, flowModeCreationProject !== FlowMode._2D3DAdvanced);
        if(flowModeCreationProject !== FlowMode._2D3DAdvanced) {
            createProject(projectData);
        }
        next();
    }, [analyseFileNames, createProject, files, flowModeCreationProject, next]);

    const dropZoneBgClass = useMemo(() => {
        if (isDragging) {
            return "border-blue-500 bg-blue-50/80";
        }
        if (files.length === 0) {
            return "border-gray-300 hover:border-gray-400 bg-gray-50/50";
        }
        return "border-gray-200 bg-white";
    }, [isDragging, files.length]);

    return (
        <div className="flex flex-col flex-1 h-full min-h-0 justify-between gap-y-4">
            <input ref={fileInputRef} type="file" multiple 
                   accept="application/pdf, .pdf" className="hidden" 
                   onChange={handleFileInputChange}/>

            <div className={`relative flex flex-col flex-1 min-h-0 rounded-lg border-2 border-dashed transition-all duration-200 ${dropZoneBgClass}`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}>

                {isDragging && (
                    <div className="absolute inset-0 z-30 flex flex-col items-center justify-center rounded-lg bg-blue-500/10 backdrop-blur-xs pointer-events-none">
                        <FontAwesomeIcon icon={faCloudArrowUp} className="text-4xl text-blue-500 mb-2 animate-bounce" />
                        <span className="text-sm font-semibold text-blue-600">
                            Déposez vos fichiers PDF ici
                        </span>
                    </div>
                )}

                {files.length === 0 ? (
                    <div className="flex flex-col items-center justify-center flex-1 p-6 text-center">
                        <div className="rounded-full bg-blue-50 flex items-center justify-center text-blue-500 size-14">
                            <FontAwesomeIcon icon={faCloudArrowUp} className="text-2xl" />
                        </div>
                        <span className="text-sm font-medium text-gray-900 mt-2 mb-5">
                            Glissez et déposez vos plans PDF ici
                        </span>

                        <Button variant="primary" size="sm" onClick={openFileDialog} className="flex flex-row items-center gap-1.5">
                            <FontAwesomeIcon icon={faPlus} className="text-xs" />
                            <span>Ajouter des plans</span>
                        </Button>
                    </div>
                ) : (
                    <div className="flex flex-col flex-1 min-h-0 p-3">
                        <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100 shrink-0">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-gray-700">
                                    {files.length} {files.length > 1 ? "plans sélectionnés" : "plan sélectionné"}
                                </span>
                            </div>
                            <Button variant="simple" size="xs" 
                                    onClick={openFileDialog} 
                                    className="flex items-center gap-1 text-green-600 hover:text-green-700" title="Ajouter des plans PDF">
                                <FontAwesomeIcon icon={faPlus} className="text-green-600" />
                                <span>Ajouter</span>
                            </Button>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-y-1.5 pr-1">
                            {files.map((file, index) => (
                                <div key={`${file.name}-${index}`}
                                    className="flex items-center justify-between p-2 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center gap-2.5 min-w-0 flex-1 mr-2">
                                        <FontAwesomeIcon icon={faFilePdf} className="text-orange-700 text-lg shrink-0" />
                                        <span className="text-xs font-medium text-gray-800 truncate min-w-0" title={file.name}>
                                            {file.name}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveFile(index)}
                                        className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors cursor-pointer shrink-0"
                                        title="Supprimer">
                                        <FontAwesomeIcon icon={faTrash} className="text-xs" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {flowModeCreationProject === FlowMode._2D3DAdvanced &&
                <div className='grid grid-cols-[auto_1fr] gap-x-4 items-center'>
                    <Label>Hauteur entre les étages</Label>
                    <Input type='number' defaultValue={storeyHeightRef.current} onInput={(e) => {
                        storeyHeightRef.current = Number((e.target as HTMLInputElement).value);
                    }}/>
                </div>
            }

            <div className="flex shrink-0 justify-between">
                <Button onClick={() => prev()}>Revenir en arrière</Button>
                <Button disabled={files.length === 0} onClick={handleClickContinue}>
                    Continuer
                </Button>
            </div>
        </div>
    );
};

export default CreateProjectImportPlansStep;
